import type {Dispatch, SetStateAction} from "react";
import React, {useCallback, useRef} from "react";

import useTimeout from "../../react/hooks/useTimeout";

export type FormContextValues<TFieldValues = Record<string, any>> = {
    setItemRef: (name: keyof TFieldValues) => (node: HTMLDivElement) => void;
    getItemRef: (name: keyof TFieldValues) => HTMLElement | undefined;
    scrollToField: (name: keyof TFieldValues) => void;
    setFieldGroup: (name: string, groupId: string) => void;
    setNavigateToGroup: (navigateToGroup: (group: string) => void) => void;
    setErrors: Dispatch<SetStateAction<any>>;
};

export const FormContext = React.createContext<FormContextValues>({
    setItemRef: () => () => {},
    getItemRef: () => undefined,
    scrollToField: () => {},
    setFieldGroup: () => {},
    setNavigateToGroup: () => {},
    setErrors: () => {}
});

export default function useForm<TFieldValues extends Record<string, any> = Record<string, any>>() {
    const fieldsRef = useRef<Partial<Record<keyof TFieldValues, HTMLDivElement>>>({});
    const mapFieldGroup = useRef<Partial<Record<keyof TFieldValues, string>>>({});
    const navigateToGroupRef = useRef<(group: string) => void>(undefined);

    const scrollIntoViewTimeout = useTimeout((el: HTMLElement) => {
        el.scrollIntoView({behavior: "auto", block: "center"});
    }, 100);

    const setItemRef = useCallback(
        (name: keyof TFieldValues) => (node: HTMLDivElement) => {
            fieldsRef.current[name] = node;
        },
        []
    );

    const getItemRef = useCallback(
        (name: keyof TFieldValues) => {
            return fieldsRef.current[name];
        },
        [fieldsRef]
    );

    const scrollToElement = useCallback(
        async (name: keyof TFieldValues, el: HTMLElement) => {
            const groupId = mapFieldGroup.current[name];
            if (groupId && navigateToGroupRef.current) {
                navigateToGroupRef.current(groupId);

                scrollIntoViewTimeout(el);
            } else {
                el.scrollIntoView({behavior: "auto", block: "center"});
            }
        },
        [scrollIntoViewTimeout]
    );

    const scrollToField = useCallback(
        (name: keyof TFieldValues) => {
            const ref = getItemRef(name);
            if (ref) {
                scrollToElement(name, ref);
            }
        },
        [getItemRef, scrollToElement]
    );

    const setFieldGroup = useCallback((name: keyof TFieldValues, group: string) => {
        mapFieldGroup.current[name] = group;
    }, []);

    const setNavigateToGroup = useCallback((navigateToGroup: (group: string) => void) => {
        navigateToGroupRef.current = navigateToGroup;
    }, []);

    return {
        scrollToField,
        setItemRef,
        getItemRef,
        scrollToElement,
        setFieldGroup,
        setNavigateToGroup
    };
}
